import React from 'react';
import { UserProfile } from '@/shared/types';
import { Eye, ArrowUpCircle, ShieldCheck, Key, Trash2, Building2, ChevronUp, ChevronDown, ExternalLink, Check } from 'lucide-react';
import { Checkbox } from '@/shared/components/ui';

type SortField = 'name' | 'email' | 'role' | 'plan' | 'joined' | 'revenue';

interface UserTableProps {
  users: UserProfile[];
  onPreviewUser: (user: UserProfile) => void;
  onInitiateAction: (type: ActionType, user: UserProfile) => void;
  // Optional: Map of user ID to total revenue (from admin service)
  userRevenues?: Record<string, number>;
  // Sorting
  sortField?: SortField;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: SortField) => void;
  // Optional: Custom view URL for navigation
  onViewDetails?: (userId: string) => void;
  // Bulk selection
  selectedUserIds?: Set<string>;
  onToggleSelection?: (userId: string) => void;
  onToggleSelectAll?: (userIds: string[]) => void;
  isAllSelected?: (userIds: string[]) => boolean;
  isSomeSelected?: (userIds: string[]) => boolean;
}

export type ActionType = 'delete' | 'reset_password' | 'toggle_company' | 'change_plan' | null;

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onPreviewUser,
  onInitiateAction,
  userRevenues = {},
  sortField = 'name',
  sortDirection = 'asc',
  onSort,
  onViewDetails,
  selectedUserIds = new Set(),
  onToggleSelection,
  onToggleSelectAll,
  isAllSelected,
  isSomeSelected,
}) => {
  const handleSort = (field: SortField) => {
    if (onSort) {
      onSort(field);
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return null;
    }
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const sortableHeader = (field: SortField, label: string) => (
    <th
      className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {renderSortIcon(field)}
      </div>
    </th>
  );

  const userIds = users.map(u => u.id);
  const allSelected = isAllSelected ? isAllSelected(userIds) : false;
  const someSelected = isSomeSelected ? isSomeSelected(userIds) : false;

  return (
    <div className="bg-white border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase">
            <tr>
              {onToggleSelectAll && (
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={() => onToggleSelectAll(userIds)}
                    aria-label="Select all users"
                  />
                </th>
              )}
              {sortableHeader('name', 'User')}
              {sortableHeader('role', 'Role')}
              {sortableHeader('plan', 'Subscription')}
              {sortableHeader('joined', 'Joined')}
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(user => {
              // Get total revenue from provided map (fetched from admin service)
              const totalEarned = userRevenues[user.id] || 0;

              return (
                <tr key={user.id} className={`hover:bg-gray-50 transition-colors group ${selectedUserIds.has(user.id) ? 'bg-indigo-50' : ''}`}>
                  {onToggleSelection && (
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selectedUserIds.has(user.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleSelection(user.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${user.name}`}
                      />
                    </td>
                  )}
                  <td
                    className="px-4 py-3 cursor-pointer"
                    onClick={() => onViewDetails ? onViewDetails(user.id) : onPreviewUser(user)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center font-medium text-gray-500 border border-gray-200 text-xs">
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          {user.name}
                          {onViewDetails && <ExternalLink size={12} className="text-gray-400" />}
                        </div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td
                    className="px-4 py-3 capitalize cursor-pointer"
                    onClick={() => onViewDetails ? onViewDetails(user.id) : onPreviewUser(user)}
                  >
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${user.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td
                    className="px-4 py-3 cursor-pointer"
                    onClick={() => onViewDetails ? onViewDetails(user.id) : onPreviewUser(user)}
                  >
                    <div className="flex items-center gap-2">
                      {user.subscription.planId === 'company' ? (
                        <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs font-medium border border-indigo-200 flex items-center gap-1">
                          <Building2 size={10}/> COMPANY
                        </span>
                      ) : (
                        <>
                          <span className={`w-1.5 h-1.5 rounded-full ${user.subscription.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                          <span className="capitalize text-gray-700 font-medium">{user.subscription.planId}</span>
                          {user.subscription.billingPeriod === 'yearly' && <span className="text-[10px] bg-indigo-50 text-indigo-600 px-1 py-0.5 rounded font-medium">YR</span>}
                        </>
                      )}
                    </div>
                  </td>
                  <td
                    className="px-4 py-3 text-gray-500 cursor-pointer"
                    onClick={() => onViewDetails ? onViewDetails(user.id) : onPreviewUser(user)}
                  >
                    {new Date(user.joinedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {user.role !== 'admin' && (
                      <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {onViewDetails && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewDetails(user.id);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="View Full Profile"
                          >
                            <ExternalLink size={14} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onPreviewUser(user);
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Quick Preview"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onInitiateAction('change_plan', user);
                          }}
                          className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                          title="Change Plan"
                        >
                          <ArrowUpCircle size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onInitiateAction('toggle_company', user);
                          }}
                          className={`p-1.5 rounded transition-colors ${user.subscription.planId === 'company' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                          title="Toggle Company Status"
                        >
                          <ShieldCheck size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onInitiateAction('reset_password', user);
                          }}
                          className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                          title="Reset Password"
                        >
                          <Key size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onInitiateAction('delete', user);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete Account"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};